<script setup lang="ts">
import DOMPurify from 'dompurify';
import { resolveReferences } from '../effects';
import { useEntryStore } from '@/stores/entry';

const entryStore = useEntryStore();

defineProps({
  description: {
    type: String,
    required: true
  }
});

const isHtml = (description: string) => {
  return description.startsWith('[!html');
};

const getHtmlContent = (description: string) => {
  let htmlContent = description.split('[!html("').slice(1)[0].split('")]')[0];

  // @ts-ignore replaceAll not recognized in some TS versions
  htmlContent = htmlContent.replaceAll('\\', '');
  return DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: ['div', 'img', 'span', 'p', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br']
  });
};

const isIframe = (description: string) => {
  return description.includes('iframe');
};

const getIframeContent = (description: string) => {
  let iframeUrl = description.split('[!(').slice(1)[0].split(')]')[0];

  iframeUrl = iframeUrl.replace('\\', '');
  return DOMPurify.sanitize(iframeUrl, {
    ALLOWED_TAGS: ['iframe']
  });
};

const isImage = (description: string) => {
  return description.includes('img');
};

const getParsedContent = (description: string) => {
  // Sanitize the HTML content to prevent XSS vulnerabilities

  const imageUrl = description
    ?.split('[!(')[1]
    ?.split('),(')[0]
    ?.replace('\\', '')
    ?.replace(')]', '');

  const heightAndWidth = description?.split('),(')[1]?.split(',');
  const width = heightAndWidth[0];
  // const height = heightAndWidth[1]?.replace(')]', '');

  const cleanHtml = DOMPurify.sanitize(imageUrl, {
    ALLOWED_TAGS: ['div', 'img']
  });

  const parsedContent = document.createElement('div');
  parsedContent.innerHTML = cleanHtml;
  const images = parsedContent.querySelectorAll('img');

  images.forEach(image => {
    image.src = image.getAttribute('src') ?
      (image.getAttribute('src') as string) :
      '';
    if (width) {
      image.style.maxWidth = `${width}px`;
    }
    image.style.width = '100%';
    image.style.objectFit = 'contain';
    // image.style.height = height ? `${height}px` : 'auto';

    image.setAttribute('alt', '');
  });

  return parsedContent.innerHTML;
};
</script>

<template>
  <div v-if="description" class="description">
    <div v-if="isHtml(description)" v-html="getHtmlContent(description)" />
    <div v-else-if="isIframe(description)" v-html="getIframeContent(description)" />
    <div class="wrap" id="wrap" v-else-if="isImage(description)">
      <div v-html="getParsedContent(description)" />
    </div>
    <div v-else>
      {{ resolveReferences(entryStore.form?.items!, description) }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.description {
  padding-top: 8px;
  color: #575e66;
}
.wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  overflow-x: auto;
}
</style>
